import React, { useState } from 'react';
import './ServicesPageContentNew.css';
import '../pages/EnterpriseSoftwarePage.css';
import {
  Brain, Building2, Factory, Globe, Cloud,
  ArrowRight, Layers, ChevronDown,
  FileText, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import { servicesData } from '../data/servicesData';

// Import service images
import careerAiCultureImg from '../assets/career/Life at Vivify & Why Join Us_images/AI & Innovation-First Culture career image.webp';
import enterpriseServiceBgImg from '../assets/Services Images/001 Services Bg1.webp';
import industrialSoftwareImg from '../assets/Services Images/03 Industrial Software.webp';
import customProductImg from '../assets/Services Images/03 Custom Softwae.webp';
import cloudItServicesImg from '../assets/Services Images/Cloud & IT Services.webp';
import erpExpandedImg from '../assets/Services Images/Hyperautomation.jpg';
import intelligenceAutomationImg from '../assets/Services Images/Intelligence Automation.jpg';
import mobileDevelopmentImg from '../assets/Services Images/mobile development.webp';

// Import title icons
import enterpriseIcon from '../assets/Explore Our Specialized Services_icons/Enterprise Software Solutions.webp';
import industrialIcon from '../assets/Explore Our Specialized Services_icons/Industrial Software & Automation.webp';
import customProductIcon from '../assets/Explore Our Specialized Services_icons/Custom Product Development.webp';
import cloudIcon from '../assets/Explore Our Specialized Services_icons/Cloud & IT Services.webp';

const titleIcons: Record<string, string> = {
  'enterprise-software-solutions': enterpriseIcon,
  'industrial-software-automation': industrialIcon,
  'custom-product-development': customProductIcon,
  'cloud-it-services': cloudIcon,
};

// Service images mapping
const serviceImages: Record<string, string> = {
  'ai-powered-solutions': careerAiCultureImg,
  'enterprise-software-solutions': enterpriseServiceBgImg,
  'industrial-software-automation': industrialSoftwareImg,
  'custom-product-development': customProductImg,
  'cloud-it-services': cloudItServicesImg,
};

// Helper to render Lucide Icons by name dynamically
export const getServiceIcon = (name: string, size: number = 24) => {
  switch (name) {
    case 'Brain': return <Brain size={size} />;
    case 'Building2': return <Building2 size={size} />;
    case 'Factory': return <Factory size={size} />;
    case 'Globe': return <Globe size={size} />;
    case 'Cloud': return <Cloud size={size} />;
    default: return <Layers size={size} />;
  }
};

// Key offerings pill tags per service
const serviceOfferings: Record<string, string[]> = {
  'ai-powered-solutions': ['AI Automation', 'LLM Chatbots', 'Predictive Analytics', 'Computer Vision'],
  'enterprise-software-solutions': ['ERP Systems', 'CRM Solutions', 'HRMS', 'Business Apps'],
  'industrial-software-automation': ['Smart Factory', 'Industrial IoT', 'OEE Monitoring', 'Predictive Maintenance'],
  'custom-product-development': ['Web Apps', 'Mobile Apps', 'SaaS Platforms', 'UI/UX Design'],
  'cloud-it-services': ['Cloud Migration', 'DevOps', 'Cybersecurity', 'Managed IT'],
};

// 3 bullet highlights per card (fills empty space with value)
const serviceHighlights: Record<string, string[]> = {
  'ai-powered-solutions': [
    'Automate repetitive tasks with autonomous AI agents',
    'Predict risks and demand with custom ML models',
    'Deploy domain-trained chatbots for support & workflows',
  ],
  'enterprise-software-solutions': [
    'Streamline business operations with secure, scalable ERP, CRM, HRMS',
    'Custom enterprise software designed to improve productivity',
    'Business performance solutions tailored to your requirements',
  ],
  'industrial-software-automation': [
    'Real-time production monitoring',
    'Smart factory and Industrial IoT solutions',
    'Intelligent process automation',
  ],
  'custom-product-development': [
    'Custom web and mobile applications',
    'Scalable SaaS and enterprise solutions',
    'User-centric product development',
  ],
  'cloud-it-services': [
    'Secure cloud infrastructure',
    'DevOps & managed IT services',
    'Enhanced performance & security',
  ],
};

// Key stat per card
const serviceStats: Record<string, { metric: string; label: string }> = {
  'ai-powered-solutions': { metric: '70%', label: 'Reduction in manual work' },
  'enterprise-software-solutions': { metric: '100%', label: 'Departments unified' },
  'industrial-software-automation': { metric: '−40%', label: 'Unplanned downtime' },
  'custom-product-development': { metric: '2×', label: 'Faster time to market' },
  'cloud-it-services': { metric: '99.9%', label: 'Uptime SLA guarantee' },
};

// Industry badges per card
const serviceIndustries: Record<string, string[]> = {
  'ai-powered-solutions': ['Manufacturing', 'Healthcare', 'Logistics', 'Retail'],
  'enterprise-software-solutions': ['Finance', 'Healthcare', 'Manufacturing', 'Real Estate'],
  'industrial-software-automation': ['Manufacturing', 'Warehousing', 'Airports', 'Utilities'],
  'custom-product-development': ['Startups', 'SaaS', 'Healthcare', 'Fintech'],
  'cloud-it-services': ['Enterprise', 'Logistics', 'Retail', 'Banking'],
};

// Contextual cross-links per spec
const contextualLinks: Record<string, { label: string; to: string } | null> = {
  'ai-powered-solutions': null,
  'enterprise-software-solutions': null,
  'industrial-software-automation': null,
  'custom-product-development': null,
  'cloud-it-services': null,
};

// First 5 services get dedicated detail pages
const detailPageIds = new Set([
  'ai-powered-solutions',
  'enterprise-software-solutions',
  'industrial-software-automation',
  'custom-product-development',
  'cloud-it-services',
]);

const expandedServiceContent: Record<string, { title: string, desc: string, points: string[], image?: string }> = {
  'custom-product-development': {
    title: 'Mobile App Development Services',
    desc: 'Build scalable, secure, and user-friendly mobile applications designed around your business goals. Our mobile app development services cover the complete product lifecycle—from UI/UX design and application development to API integration, testing, deployment, and ongoing improvements. We develop mobile solutions that help businesses improve customer experiences, streamline operations, and bring digital products to market faster.',
    points: [
      'iOS & Android App Development',
      'Cross-Platform Mobile Applications',
      'UI/UX Design & Prototyping',
      'API & Third-Party Integrations',
      'App Testing & Deployment',
      'Maintenance & Continuous Improvements'
    ],
    image: mobileDevelopmentImg
  },
  'enterprise-software-solutions': {
    title: 'ERP & Hyperautomation Solutions',
    desc: 'Modernize business operations with integrated ERP and hyperautomation solutions. We connect enterprise systems, workflows, and business processes to reduce repetitive manual work, improve operational visibility, and help teams make faster, data-driven decisions. Our solutions can be tailored to your existing technology environment and business requirements.',
    points: [
      'ERP Implementation & Customization',
      'Business Workflow Automation',
      'ERP & Third-Party System Integration',
      'Automated Data & Process Management',
      'Real-Time Business Insights',
      'Custom Enterprise Applications'
    ],
    image: erpExpandedImg
  },
  'industrial-software-automation': {
    title: 'Intelligent & Pervasive Automation',
    desc: 'Extend automation across connected business and industrial processes with intelligent, pervasive automation solutions. We help organizations identify repetitive workflows, connect systems and data, and automate processes across operations. This can improve efficiency, reduce manual intervention, increase visibility, and support faster business decisions.',
    points: [
      'Business Process Automation',
      'Workflow Automation',
      'Industrial Process Automation',
      'ERP & System Automation',
      'Industrial IoT Integration',
      'Real-Time Monitoring & Insights'
    ],
    image: intelligenceAutomationImg
  }
};

interface ServicesProps {
  preview?: boolean;
}

export const ServicesPageContent: React.FC<ServicesProps> = ({ preview: _preview = false }) => {
  const [openFaqIdx, setOpenFaqIdx] = useState<number | null>(null);

  const faqs = [
    {
      q: 'What technology services does Vivify Soft Tech provide?',
      a: 'We provide AI software services, enterprise software services, cloud IT services, industrial automation services, and custom software development.'
    },
    {
      q: 'How do I choose the right technology solution?',
      a: 'As a technology services company, we recommend the best AI, enterprise, or cloud solution for your business.'
    },
    {
      q: 'Do you offer custom software development?',
      a: 'Yes. We build custom software development solutions tailored to your business needs.'
    },
    {
      q: 'Can your solutions integrate with existing systems?',
      a: 'Yes. Our enterprise software and cloud IT services integrate seamlessly with your existing systems.'
    },
    {
      q: 'Why choose Vivify Soft Tech?',
      a: 'We understand your business first, then deliver AI-powered, scalable solutions that drive real results.'
    },
    {
      q: 'Do you provide mobile app development services?',
      a: 'Yes. We develop scalable mobile applications for iOS and Android, including custom application development, UI/UX, integrations, testing, deployment, and ongoing support.'
    },
    {
      q: 'Do you provide ERP and hyperautomation solutions?',
      a: 'Yes. We provide ERP and enterprise automation solutions that help businesses connect systems, automate workflows, reduce repetitive tasks, and improve operational visibility.'
    },
    {
      q: 'What processes can be automated?',
      a: 'We can help automate repetitive business and industrial processes, including workflows, data handling, system integrations, ERP processes, production monitoring, and other operational activities based on business requirements.'
    }
  ];


  return (
    <div className="services-page-main-wrapper">

      {/* ─── Hero Section (matches SecurAI / enterprise-hero style) ─── */}
      <section
        className="enterprise-hero"
        aria-label="Services Hero"
        style={{ position: 'relative' }}
      >
        <img 
          src="/ERP-system.webp" 
          alt="" 
          fetchPriority="high" 
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: 'center right',
            zIndex: 0
          }}
        />
        <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(75deg, rgba(5, 9, 21, 0.85) 0%, rgba(5, 9, 21, 0.85) 60%, rgba(5, 9, 21, 0) 60.1%, rgba(5, 9, 21, 0) 100%)',
            zIndex: 1
        }} />
        <div className="enterprise-hero-bg" style={{ zIndex: 2 }}>
          <div className="enterprise-hero-grid" />
          <div className="enterprise-hero-orb-1" style={{ opacity: 0.5 }} />
          <div className="enterprise-hero-orb-2" style={{ opacity: 0.5 }} />
        </div>

        <div className="container enterprise-hero-inner" style={{ position: 'relative', zIndex: 3 }}>
          <div className="enterprise-hero-content services-enterprise-hero-content">
            <h1 className="enterprise-hero-h1">
              Technology Services That Power{' '}
              <span className="accent">Smarter Business Growth</span>
            </h1>

            <p className="enterprise-hero-meta">
              Build AI solutions that automate workflows, simplify operations, and help you make better business decisions with real-time insights.
            </p>

            <div className="enterprise-hero-actions">
              <Link to="/solutions" className="enterprise-hero-primary-btn">
                Explore Solutions
              </Link>
              <Link to="/products" className="enterprise-hero-primary-btn">
                View Products
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Services Cards Section ─── */}
      <section className="services-grid-section" id="services-directory">
        <div className="container">

          {/* Centered Section Header */}
          <div className="services-section-header">
            <span className="section-tag">Service Directory</span>
            <h2 className="services-section-title">
              Explore Our <span className="text-gradient-purple">Specialized Services</span>
            </h2>
            <p className="services-section-desc">
              Each service is backed by dedicated engineering teams, defined delivery milestones, and measurable performance targets.
            </p>
          </div>

          {/* Service Cards — separated image and content like About page */}
          <div className="services-cards-list">
            {servicesData
              .filter(service => service.id !== 'ai-powered-solutions')
              .map((service, idx) => {
                const offerings = serviceOfferings[service.id] ?? [];
                const highlights = serviceHighlights[service.id] ?? [];
                const stat = serviceStats[service.id];
                const industries = serviceIndustries[service.id] ?? [];
                const ctxLink = contextualLinks[service.id] ?? null;
                const hasPage = detailPageIds.has(service.id);
                const stepNum = String(idx + 1).padStart(2, '0');
                const serviceImage = serviceImages[service.id] || careerAiCultureImg;

                const isReverse = false;

                return (
                  <React.Fragment key={service.id}>
                    <div className={`svc-overview-layout ${isReverse ? 'svc-overview-layout-reversed' : ''}`}>
                    {/* Image Side */}
                    <div className="svc-overview-image-side">
                      {/* Step Number Badge */}
                      <div className="svc-h-step">{stepNum}</div>
                      <img loading="lazy" src={serviceImage} alt={service.title} className="svc-overview-img"  title="Vivify Soft Tech" />
                      {/* Stat pill overlaid on image */}
                      {stat && (
                        <div className="svc-h-stat-badge">
                          <span className="svc-stat-metric">{stat.metric}</span>
                          <span className="svc-stat-label">{stat.label}</span>
                        </div>
                      )}
                    </div>

                    {/* Content Side */}
                    <div className="svc-overview-content-side">
                      <h2 className="svc-h-title" style={{ display: 'flex', alignItems: 'center' }}>
                        {titleIcons[service.id] && (
                          <img loading="lazy" src={titleIcons[service.id]} 
                            alt="" 
                            style={{ width: '32px', height: '32px', objectFit: 'contain', marginRight: '12px', flexShrink: 0 }} 
                           title="Vivify Soft Tech" />
                        )}
                        {service.title.replace('💼 ', '').replace('🏭 ', '').replace('🚀 ', '').replace('☁️ ', '')}
                      </h2>
                      <p className="svc-h-desc">{service.description}</p>

                      {/* Bullet highlights */}
                      {highlights.length > 0 && (
                        <ul className="svc-h-highlights">
                          {highlights.map((h, i) => (
                            <li key={i} className="svc-h-highlight-item">
                              <CheckCircle2 size={13} className="svc-h-check" />
                              <span>{h}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Industries served */}
                      {industries.length > 0 && (
                        <div className="svc-h-industries">
                          <span className="svc-h-industries-label">Industries:</span>
                          <div className="svc-h-industry-pills">
                            {industries.map((ind, i) => (
                              <span key={i} className="svc-h-industry-badge">{ind}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Offering tags + footer */}
                      {offerings.length > 0 && (
                        <div className="svc-h-tags">
                          {offerings.map((o, i) => (
                            <span key={i} className="service-offering-tag">{o}</span>
                          ))}
                        </div>
                      )}

                      <div className="svc-h-footer">
                        {hasPage ? (
                          <Link to={`/services/${service.id}`} className="service-card-link">
                            Learn More <ArrowRight size={14} />
                          </Link>
                        ) : (
                          <span className="service-card-link-muted">Section overview</span>
                        )}
                        {ctxLink && (
                          <Link to={ctxLink.to} className="service-ctx-link">
                            {ctxLink.label}
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content rendered as its own separate card layout */}
                  {expandedServiceContent[service.id] && (
                      <div className={`svc-overview-layout ${!isReverse ? 'svc-overview-layout-reversed' : ''}`} style={{ marginTop: '24px' }}>
                        <div className="svc-overview-image-side">
                          {expandedServiceContent[service.id].image && (
                            <img loading="lazy" src={expandedServiceContent[service.id].image} alt="Expanded content" className="svc-overview-img" style={{ borderRadius: '16px', objectFit: 'cover', width: '100%', height: '100%' }}  title="Vivify Soft Tech" />
                          )}
                        </div>
                        <div className="svc-overview-content-side" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <h2 className="svc-h-title" style={{ display: 'flex', alignItems: 'center', fontSize: '1.75rem', marginBottom: '16px' }}>
                            {expandedServiceContent[service.id].title}
                          </h2>
                          <p className="svc-h-desc" style={{ marginBottom: '24px' }}>
                            {expandedServiceContent[service.id].desc}
                          </p>
                          <ul className="svc-expanded-points">
                            {expandedServiceContent[service.id].points.map((pt, i) => (
                              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                                <CheckCircle2 size={16} style={{ color: 'var(--primary-color, #8b5cf6)', marginRight: '8px', marginTop: '3px', flexShrink: 0 }} />
                                <span>{pt}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
          </div>

        </div>
      </section>

      {/* ─── FAQ Section ─── */}
      <section className="services-faq-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-tag">FAQ</span>
            <h2 className="section-title">Common <span className="text-gradient-purple">Questions Answered</span></h2>
            <p>Everything you need to know before starting a project with our engineering teams.</p>
          </div>

          <div className="faq-accordion-container delay-1">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`faq-accordion-item${openFaqIdx === idx ? ' open' : ''}`}
              >
                <button
                  className="faq-trigger-btn"
                  onClick={() => setOpenFaqIdx(openFaqIdx === idx ? null : idx)}
                >
                  <span>{faq.q}</span>
                  <ChevronDown size={18} className="faq-trigger-chevron" />
                </button>
                <div className="faq-content-pane">
                  <div className="faq-content-inner">
                    <p>{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA Section ─── */}
      <section className="services-cta-section blueprint-bg">
        <div className="scanline-overlay" />
        <div className="container relative-z">
          <div className="services-cta-panel">
            <h2 className="services-cta-title">
              Let's Build Smarter <br />
              <span className="text-gradient-purple">Technology Together</span>
            </h2>
            <p className="services-cta-desc">
              Partner with a Vivify Soft Tech services company for AI software, enterprise solutions, and cloud IT services tailored to your business goals.
            </p>
            <div className="services-cta-actions">
              <Link to="/contact">
                <Button variant="primary" size="lg" icon={<ArrowRight size={16} />}>
                  Book a consultation
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="primary" size="lg" icon={<FileText size={16} />}>
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPageContent;
